import { motion } from 'framer-motion';
import {
  Target,
  TrendingUp,
  Calendar,
  CheckCircle,
  ArrowRight,
  Zap,
  BarChart3,
  Users,
  Compass,
  AlertCircle,
  Award,
} from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import MarketingLayout from '../../components/MarketingLayout';
import Breadcrumb from '../../components/Breadcrumb';
import PageHero from '../../components/PageHero';
import { getPageHeroImageUrl } from '../../config/cloudflareAssets';

export default function StrategicObjectivesPage() {
  const location = useLocation();
  const heroImageUrl = getPageHeroImageUrl(location.pathname);
  const smartCriteria = [
    {
      letter: 'S',
      word: 'Specific',
      description: 'Clear and unambiguous about what will be achieved—no vague aspirations',
      example: 'Increase market share in the SMB segment from 15% to 25%',
      questions: [
        'Who is responsible?',
        'What exactly will be accomplished?',
        'Which market/segment?',
      ],
    },
    {
      letter: 'M',
      word: 'Measurable',
      description: 'Quantifiable with metrics that track progress objectively',
      example: 'From 15% to 25% market share (10 percentage point increase)',
      questions: [
        'How will we measure success?',
        "What's the starting point?",
        "What's the target?",
      ],
    },
    {
      letter: 'A',
      word: 'Achievable',
      description: 'Challenging but realistic given resources, capabilities, and market conditions',
      example: 'Based on current 15% annual growth rate and planned $5M investment',
      questions: [
        'Do we have necessary resources?',
        'Is this realistic given constraints?',
        "What's our track record?",
      ],
    },
    {
      letter: 'R',
      word: 'Relevant',
      description: 'Aligned with vision, mission, and overall strategy—not a distraction',
      example: 'Supports mission to become market leader in mid-market strategic planning',
      questions: [
        'Does this support our vision?',
        'Is this a priority right now?',
        'Why does this matter?',
      ],
    },
    {
      letter: 'T',
      word: 'Time-bound',
      description: 'Clear deadline for achievement creates urgency and enables tracking',
      example: 'By end of Q4 2027 (24-month timeline)',
      questions: [
        'When will this be completed?',
        'What are key milestones?',
        'Is the timeline realistic?',
      ],
    },
  ];

  const objectiveExamples = [
    {
      category: 'Revenue & Growth',
      icon: TrendingUp,
      objectives: [
        { text: 'Achieve $50M in ARR by Q4 2027', why: 'Signals scale and market validation' },
        {
          text: 'Expand to 5 new geographic markets within 24 months',
          why: 'Diversifies revenue and reduces regional risk',
        },
        {
          text: 'Grow customer base from 500 to 2,000 enterprise clients',
          why: 'Builds predictable recurring revenue base',
        },
        {
          text: 'Increase average contract value from $25K to $40K',
          why: 'Improves unit economics and profitability',
        },
      ],
    },
    {
      category: 'Operational Excellence',
      icon: Award,
      objectives: [
        {
          text: 'Reduce customer churn from 8% to 3% annually',
          why: 'Churn is silent killer of SaaS growth',
        },
        { text: 'Improve NPS score from 45 to 70+', why: 'Drives referrals and organic growth' },
        {
          text: 'Decrease operational costs by 25% through automation',
          why: 'Improves margins without sacrificing quality',
        },
        {
          text: 'Achieve 95% customer support satisfaction rating',
          why: 'Service excellence differentiates us',
        },
      ],
    },
    {
      category: 'Product & Innovation',
      icon: Zap,
      objectives: [
        {
          text: 'Launch 3 major product features per quarter',
          why: 'Maintains competitive advantage and engagement',
        },
        {
          text: 'Achieve 90% customer satisfaction with new features',
          why: "Ensures we're building what customers value",
        },
        {
          text: 'Reduce time-to-market for new features by 40%',
          why: 'Speed is competitive advantage in fast-moving markets',
        },
        {
          text: 'Increase product usage from 2x to 5x per week per user',
          why: 'Engagement drives retention and expansion',
        },
      ],
    },
    {
      category: 'People & Culture',
      icon: Users,
      objectives: [
        {
          text: 'Build engineering team from 20 to 50 people',
          why: 'Product velocity requires talent density',
        },
        {
          text: 'Achieve 90%+ employee engagement score',
          why: 'Engaged employees drive customer success',
        },
        {
          text: 'Establish leadership development program for 100 managers',
          why: 'Scale requires scalable leadership',
        },
        {
          text: 'Reduce time-to-hire from 90 to 45 days',
          why: 'Fast hiring captures best talent before competitors',
        },
      ],
    },
  ];

  return (
    <MarketingLayout>
      <div className="bg-gray-50 border-b border-gray-200">
        <div className="container mx-auto px-4 py-3">
          <Breadcrumb
            items={[
              { label: 'Strategic Framework', path: '/strategic-framework' },
              { label: 'Foundation', path: '/foundation' },
              { label: 'Strategic Objectives' },
            ]}
          />
        </div>
      </div>

      <PageHero
        heroImageUrl={heroImageUrl}
        badge={
          <span className="inline-flex items-center px-4 py-2 bg-white/90 text-blue-800 rounded-full text-sm font-semibold backdrop-blur-sm">
            <Compass className="w-4 h-4 mr-2" />
            Foundation Step 4 of 4
          </span>
        }
        title="Turn your ambition into clear objectives"
        subtitle="Give your strategy measurable direction. Choose the outcomes that matter, define success, and make the next few years of work easier to prioritize."
      >
        <Link
          to="/sign-up"
          className="public-primary-button inline-flex items-center bg-blue-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:opacity-90"
        >
          Set your objectives
        </Link>
      </PageHero>

      {/* Why They Matter */}
      <section className="public-section py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl font-bold mb-6">Make progress something you can evaluate</h2>
              <p className="text-xl text-gray-600">
                A vision is a destination; objectives give you the milestones. Decide what must
                change, how you will measure it, and when you expect to get there.
              </p>
            </motion.div>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                {
                  icon: Target,
                  title: 'Focus Scarce Resources',
                  description:
                    'Organizations have limited time, money, and attention. Objectives force you to choose what matters most and say "no" to distractions.',
                  impact: 'Teams know where to invest effort for maximum impact',
                },
                {
                  icon: TrendingUp,
                  title: 'Create Alignment',
                  description:
                    'Clear objectives align everyone from executives to individual contributors on what success looks like and when it must be achieved.',
                  impact: 'Reduces wasted effort on misaligned activities',
                },
                {
                  icon: BarChart3,
                  title: 'Enable Measurement',
                  description:
                    'You can\'t manage what you don\'t measure. Objectives create accountability by defining what "winning" looks like objectively.',
                  impact: 'Progress tracking, course correction, and learning',
                },
                {
                  icon: Calendar,
                  title: 'Drive Urgency',
                  description:
                    'Time-bound objectives create urgency. Without deadlines, important work gets delayed indefinitely by "urgent" daily tasks.',
                  impact: 'Prevents strategic drift and ensures execution',
                },
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={false}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="public-card bg-white p-6 rounded-xl border border-gray-200"
                >
                  <div className="flex items-start space-x-3 mb-3">
                    <div className="bg-blue-100 p-2 rounded-lg">
                      <item.icon className="w-6 h-6 text-blue-600" />
                    </div>
                    <h3 className="text-xl font-bold">{item.title}</h3>
                  </div>
                  <p className="text-gray-700 mb-3 text-sm">{item.description}</p>
                  <div className="bg-green-50 p-3 rounded-lg border-l-4 border-green-500">
                    <p className="text-xs text-gray-700">
                      <span className="font-semibold text-green-700">Impact:</span> {item.impact}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SMART Framework */}
      <section className="public-section py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={false}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold mb-6">The SMART Framework</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Every strategic objective should meet these five criteria—no exceptions
            </p>
          </motion.div>
          <div className="space-y-5 max-w-5xl mx-auto">
            {smartCriteria.map((criteria, i) => (
              <motion.div
                key={i}
                initial={false}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="public-card bg-white p-6 rounded-xl border border-gray-200"
              >
                <div className="flex items-start space-x-4">
                  <div className="bg-blue-600 text-white w-14 h-14 rounded-full flex items-center justify-center text-2xl font-bold flex-shrink-0">
                    {criteria.letter}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold mb-2">{criteria.word}</h3>
                    <p className="text-gray-700 mb-3">{criteria.description}</p>
                    <div className="bg-blue-50 p-3 rounded-lg mb-3">
                      <p className="text-sm text-gray-700">
                        <span className="font-semibold text-blue-700">Example:</span>{' '}
                        {criteria.example}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-gray-900 mb-2">
                        Critical Questions:
                      </p>
                      <ul className="space-y-1">
                        {criteria.questions.map((q, j) => (
                          <li key={j} className="text-xs text-gray-600 flex items-start">
                            <CheckCircle className="w-3 h-3 text-green-500 mr-1.5 mt-0.5 flex-shrink-0" />
                            {q}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
          <motion.div
            initial={false}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="public-card max-w-3xl mx-auto mt-8 p-6 bg-blue-50 rounded-xl border border-gray-200"
          >
            <p className="text-gray-700 text-center">
              <span className="font-semibold text-blue-700">Pro Tip:</span> If an objective fails
              any SMART criterion, it's not ready. Refine it until it passes all five tests.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Examples by Category */}
      <section className="public-section py-16 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={false}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold mb-6">Strategic Objective Examples by Category</h2>
            <p className="text-xl text-gray-600">
              Real-world objectives across different business focus areas
            </p>
          </motion.div>
          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {objectiveExamples.map((cat, i) => (
              <motion.div
                key={i}
                initial={false}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="public-card bg-white p-8 rounded-xl border border-gray-200"
              >
                <div className="flex items-center space-x-3 mb-6">
                  <div className="bg-blue-600 p-3 rounded-lg">
                    <cat.icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-blue-600">{cat.category}</h3>
                </div>
                <div className="space-y-4">
                  {cat.objectives.map((obj, j) => (
                    <div key={j} className="border-l-4 border-blue-500 pl-4 py-2">
                      <p className="text-gray-900 font-medium mb-1">{obj.text}</p>
                      <p className="text-xs text-gray-600">
                        <span className="font-semibold">Why:</span> {obj.why}
                      </p>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How to Set Objectives */}
      <section className="public-section py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl font-bold mb-6">How to Set Strategic Objectives</h2>
              <p className="text-xl text-gray-600">
                A systematic process for defining objectives that drive results
              </p>
            </motion.div>
            <div className="space-y-6">
              {[
                {
                  step: '1',
                  title: 'Start with Vision & Mission',
                  description:
                    'Objectives must support your vision and mission. Ask: If we achieve our vision in 10 years, what must be true in 3-5 years? Those milestones become objectives.',
                  tip: 'Work backward from vision. If vision is "global leader in X," objectives might include market share, geographic presence, and brand recognition.',
                },
                {
                  step: '2',
                  title: 'Analyze Strategic Position',
                  description:
                    'Review your SWOT, market analysis, and competitive position. Objectives should leverage strengths, address weaknesses, capture opportunities, and mitigate threats.',
                  tip: 'Ground objectives in reality. Ambitious is good; delusional is not. Market research prevents wishful thinking.',
                },
                {
                  step: '3',
                  title: 'Limit to 3-7 Objectives',
                  description:
                    'Focus is power. Most organizations can only execute 3-7 strategic objectives simultaneously. More than that dilutes attention and resources.',
                  tip: 'Use 80/20 rule: Which 3-7 objectives will drive 80% of strategic value? Ruthlessly deprioritize everything else.',
                },
                {
                  step: '4',
                  title: 'Balance Different Dimensions',
                  description:
                    "Don't set all objectives in one area (e.g., all financial). Balance across growth, operations, product, customer, and people. This creates sustainable success.",
                  tip: 'Use Balanced Scorecard framework: Financial, Customer, Internal Process, Learning & Growth. Ensures holistic strategy.',
                },
                {
                  step: '5',
                  title: 'Cascade to Departments',
                  description:
                    'Break company-level objectives into department/team-level goals. Each function should understand their role in achieving corporate objectives.',
                  tip: 'Ask each department: "What must you accomplish to enable this corporate objective?" Ensures alignment and accountability.',
                },
                {
                  step: '6',
                  title: 'Review and Adjust Quarterly',
                  description:
                    "Strategic objectives aren't set-and-forget. Review progress quarterly, celebrate wins, diagnose shortfalls, and adjust if market conditions fundamentally change.",
                  tip: 'Change objectives only for major strategic shifts, not minor setbacks. Consistency creates credibility and focus.',
                },
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={false}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="public-card bg-white p-6 rounded-xl border border-gray-200"
                >
                  <div className="flex items-start space-x-4">
                    <div className="bg-blue-600 text-white w-12 h-12 rounded-full flex items-center justify-center font-bold text-xl flex-shrink-0">
                      {item.step}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                      <p className="text-gray-700 mb-3">{item.description}</p>
                      <div className="bg-blue-50 p-3 rounded-lg border-l-4 border-blue-500">
                        <p className="text-sm text-gray-700">
                          <span className="font-semibold text-blue-700">Pro Tip:</span> {item.tip}
                        </p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Common Mistakes */}
      <section className="public-section py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl font-bold mb-6">Common Strategic Objective Mistakes</h2>
              <p className="text-xl text-gray-600">Avoid these traps that undermine execution</p>
            </motion.div>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                {
                  mistake: 'Too Many Objectives',
                  description:
                    'Setting 15-20 strategic objectives. Result: Nothing is truly strategic. Resources spread thin, teams confused about priorities.',
                  fix: 'Limit to 3-7 objectives maximum. If everything is a priority, nothing is. Brutal prioritization clarifies strategy and execution.',
                },
                {
                  mistake: 'Activities Not Outcomes',
                  description:
                    '"Launch new marketing campaign" is an activity, not an objective. Objective is the outcome: "Increase brand awareness from 20% to 40%."',
                  fix: 'Objectives describe end state, not actions. They answer "what outcome" not "what we\'ll do." Activities belong in initiative planning.',
                },
                {
                  mistake: 'Not Measurable',
                  description:
                    '"Improve customer satisfaction" is vague. Improve by how much? From what baseline? By when? Without metrics, no accountability.',
                  fix: "Every objective needs specific, quantifiable target and deadline. If you can't measure it, you can't manage it.",
                },
                {
                  mistake: 'Sandbagging or Wishful Thinking',
                  description:
                    "Setting objectives so easy you'll definitely hit them (sandbagging) or so impossible they're demotivating (wishful thinking).",
                  fix: "Objectives should be ambitious but achievable—70-80% confidence you'll hit them. Stretch goals motivate; impossible goals demoralize.",
                },
                {
                  mistake: 'Set Once, Never Revisited',
                  description:
                    'Set objectives in annual planning retreat, then forget them. No tracking, no reviews, no adjustments. They become irrelevant.',
                  fix: 'Review objectives quarterly minimum. Track progress, celebrate wins, diagnose gaps, adjust tactics (rarely adjust objectives themselves).',
                },
                {
                  mistake: 'Not Linked to Initiatives',
                  description:
                    'Clear objectives but no plan to achieve them. Objectives without initiatives are wishes. Initiatives without objectives are busywork.',
                  fix: 'For each objective, define 2-5 strategic initiatives that will drive achievement. Clear line from objective → initiative → project → task.',
                },
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={false}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="public-card bg-white p-6 rounded-xl border border-red-200"
                >
                  <div className="flex items-start space-x-3 mb-3">
                    <AlertCircle className="w-6 h-6 text-red-500 flex-shrink-0" />
                    <h3 className="text-lg font-bold text-gray-900">{item.mistake}</h3>
                  </div>
                  <p className="text-gray-700 mb-3 text-sm">{item.description}</p>
                  <div className="bg-green-50 p-3 rounded-lg border-l-4 border-green-500">
                    <p className="text-sm text-gray-700">
                      <span className="font-semibold text-green-700">✓ Fix:</span> {item.fix}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Put the framework to work */}
      <section className="public-section py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <p className="text-sm font-semibold text-blue-700 mb-3">
                YOUR NEXT STEP IN STRATPLAN
              </p>
              <h2 className="text-3xl font-bold mb-5">
                Create objectives your plan can work toward
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Bring this framework into your company’s plan. Work through the questions, record
                your decisions, and connect them to the strategy you are building.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="public-card bg-white p-6 rounded-xl border border-gray-200">
                <p className="text-sm font-semibold text-blue-700 mb-3">01</p>
                <h3 className="text-xl font-bold mb-3">Choose the outcome</h3>
                <p className="text-gray-600">
                  Focus on a small set of changes that would meaningfully advance your strategy.
                </p>
              </div>
              <div className="public-card bg-white p-6 rounded-xl border border-gray-200">
                <p className="text-sm font-semibold text-blue-700 mb-3">02</p>
                <h3 className="text-xl font-bold mb-3">Define the evidence</h3>
                <p className="text-gray-600">
                  Add a measure, a starting point, and a timeframe so progress is open to review.
                </p>
              </div>
              <div className="public-card bg-white p-6 rounded-xl border border-gray-200">
                <p className="text-sm font-semibold text-blue-700 mb-3">03</p>
                <h3 className="text-xl font-bold mb-3">Connect the work</h3>
                <p className="text-gray-600">
                  Use each objective to judge which initiatives deserve attention and resources.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Topics */}
      <section className="public-section py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-center">Related Strategic Tools</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <Link
                to="/execution-monitoring/okrs-kpis"
                className="public-card bg-white p-6 rounded-xl border border-gray-200 hover:border-gray-200 transition-all group"
              >
                <BarChart3 className="w-8 h-8 text-blue-600 mb-3" />
                <h3 className="text-xl font-bold mb-2 text-blue-600">OKRs & KPIs</h3>
                <p className="text-gray-600 text-sm mb-3">
                  Learn how to measure and track objectives with key results and performance
                  indicators
                </p>
                <div className="flex items-center text-blue-600 font-semibold text-sm group-hover:translate-x-1 transition-transform">
                  Learn More <ArrowRight className="w-4 h-4 ml-2" />
                </div>
              </Link>
              <Link
                to="/strategy-formulation/strategic-initiatives"
                className="public-card bg-white p-6 rounded-xl border border-gray-200 hover:border-gray-200 transition-all group"
              >
                <Zap className="w-8 h-8 text-purple-600 mb-3" />
                <h3 className="text-xl font-bold mb-2 text-purple-600">Strategic Initiatives</h3>
                <p className="text-gray-600 text-sm mb-3">
                  Turn objectives into actionable initiatives with clear owners and timelines
                </p>
                <div className="flex items-center text-purple-600 font-semibold text-sm group-hover:translate-x-1 transition-transform">
                  Learn More <ArrowRight className="w-4 h-4 ml-2" />
                </div>
              </Link>
              <Link
                to="/execution-monitoring/strategic-cadence"
                className="public-card bg-white p-6 rounded-xl border border-gray-200 hover:border-gray-200 transition-all group"
              >
                <Calendar className="w-8 h-8 text-indigo-600 mb-3" />
                <h3 className="text-xl font-bold mb-2 text-indigo-600">Review Cadence</h3>
                <p className="text-gray-600 text-sm mb-3">
                  Establish regular review rhythms to monitor progress and stay on track
                </p>
                <div className="flex items-center text-indigo-600 font-semibold text-sm group-hover:translate-x-1 transition-transform">
                  Learn More <ArrowRight className="w-4 h-4 ml-2" />
                </div>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Navigation */}
      <section className="public-section py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/foundation/core-values"
                className="inline-flex items-center justify-center bg-white text-gray-700 px-6 py-3 rounded-lg font-semibold border border-gray-300 hover:bg-gray-50"
              >
                ← Previous: Core Values
              </Link>
              <Link
                to="/external-assessment"
                className="inline-flex items-center justify-center bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700"
              >
                Next Step: External Assessment <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Start your plan */}
      <section className="public-section public-cta py-16 bg-gray-50">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-5">
            Set your objectives in StratPlan
          </h2>
          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
            Use each objective to judge which initiatives deserve attention and resources. Start
            with your company’s context and build a plan you can put to work.
          </p>
          <Link
            to="/sign-up"
            className="public-primary-button inline-flex items-center gap-2 bg-blue-600 text-white px-7 py-3 rounded-full font-semibold hover:bg-blue-700"
          >
            Set your objectives <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </MarketingLayout>
  );
}
